/* Open modal with map where 1 location is shown */

import { nonEmptyArray, option, record } from "fp-ts";
import { Option } from "fp-ts/Option";
import { Language } from "../models/language";
import { Map, MapSet, Point } from "../models/map";
import { ApiService } from "../services/api-service";
import { pipe } from "fp-ts/lib/function";
import { MapView } from "../components/mapView/mapView";
import * as RX from "rxjs";
import { ModalComponent } from "../components/modal/modal.component";
import { Failure } from "../helpers/failure";
import { getClicks$ } from "../helpers/rxjs-helpers";
import * as LMNt from "../helpers/dom-element-helpers";
import { App } from "./app";
import { UGElement, UGLocationData } from "../models/module";

type MapLinkAttributes = {
  readonly exhibitionId: number;
  readonly locationId: number;
  readonly markerClass: string;
};

/**
 * Sets up a global listener for clicks on elements of the following form
 * Clicking one opens a modal with the given location ID highlighted on a map
 *
 * The marker class will be applied to the marker on the map in the modal.
 * More than one marker class can be selected
 *
 * <a class="open_mapview_modal"
 *    data-exhibition-id="321"
 *    data-location-id="2611"
 *    data-marker-class="my-class my-other-class"
 * >
 *    link text
 * </a>
 * @param exhibitionId
 * @param language
 * @param apiService
 * @returns
 */
export function SetupMapViewLinks(
  language: Language,
  apiService: ApiService,
  clientSpecificClass: string
) {
  const linkClick$ = getClicks$(".open_mapview_modal").pipe(
    RX.map(decodeElementAttributes),
    RX.filter(option.isSome),
    RX.map((x) => x.value)
  );

  const mapData$ = linkClick$.pipe(RX.switchMap(getMapDataForAttributes));

  mapData$.subscribe(({ attributes, mapSet, locations }) =>
    elementClickHandler(attributes, mapSet, locations, clientSpecificClass)
  );
}

function getMapDataForAttributes(attributes: MapLinkAttributes) {
  return App.getExhibitionFromId(attributes.exhibitionId).pipe(
    RX.map((exhibition) => {
      return {
        attributes: attributes,
        mapSet: exhibition.mapSet,
        locations: pipe(
          exhibition.modules,
          record.lookup("location"),
          option.getOrElseW(() => Failure("No locations found on exhibition")),
          (module) => module.elements
        ),
      };
    })
  );
}

function constructMapSet(maps: Map[], initialMap: Map): MapSet {
  return { initialMap, maps };
}

function getLocationById(
  locationId: number,
  locations: UGElement[]
): Option<UGElement> {
  return option.fromNullable(
    locations.find((location) => location.id == locationId)
  );
}

function showMapModal(
  mapSet: MapSet,
  point: Point,
  clientSpecificClass: string
) {
  const myModal = ModalComponent.openModalWithUrlState(
    `<div class="map modal">
       <div class="ug map-view"></div>
       <div class="modal__buttons_container">
        <div class="ug_knap right">
          <a class="modal__btn modal_close">Luk</a>
        </div>
      </div>
    </div>`,
    clientSpecificClass,
    "loc:" + point.id
  );

  myModal.getContent().querySelector(".modal_close");

  const mapContainer =
    myModal.getContent().querySelector<HTMLElement>(".map-view") ??
    Failure("Ought not happen");
  MapView.render(
    mapContainer,
    mapSet,
    [point],
    false,
    (_) => {},
    (_) => {}
  );
}

function pointFromLocationModule(
  locationModule: UGElement,
  markerClass: string
): Point {
  const location = locationModule.locationData;

  return {
    id: location.id,
    mapId: location.mapId,
    x: location.x,
    y: location.y,
    iconClass: markerClass,
    iconValue: "",
    locationName: location.name,
    pathCustomIcon: location.pathCustomIcon,
    pathCustomIcons: location.pathCustomIcons,
    elementLogo: location.elementLogo,
    elementTitle: location.elementTitle,
  };
}

function getInitialMapFromLocation(
  locationModule: UGElement,
  mapSet: MapSet
): Option<Map> {
  // returns the map of the first location which has a map in the maps array
  const location = locationModule.locationData;

  // Find the first map that matches the location's mapId
  const map = mapSet.maps.find((map) => map.id === location.mapId);

  // Return the map wrapped in an Option type
  return map ? option.some(map) : option.none;
}

const decodeElementAttributes = (
  element: Element
): option.Option<MapLinkAttributes> => {
  return pipe(
    option.of({}),
    option.bind("exhibitionId", () =>
      LMNt.getIntAttribute("data-exhibition-id")(element)
    ),
    option.bind("locationId", () =>
      LMNt.getIntAttribute("data-location-id")(element)
    ),
    option.bind("markerClass", () =>
      LMNt.getAttribute("data-marker-class")(element)
    )
  );
};

function elementClickHandler(
  attributes: MapLinkAttributes,
  maps: MapSet,
  locations: UGElement[],
  clientSpecificClass: string
) {
  pipe(
    option.of({ maps, locations, ...attributes }),
    option.bind("location", ({ locationId, locations }) =>
      getLocationById(locationId, locations)
    ),
    option.bind("initialMap", ({ location, maps }) =>
      getInitialMapFromLocation(location, maps)
    ),
    option.bind("mapSet", ({ maps, initialMap }) =>
      option.of(constructMapSet(maps.maps, initialMap))
    ),
    option.bind("point", ({ location, markerClass }) => {
      const point = pointFromLocationModule(location, markerClass);
      return option.of(point);
    }),
    option.fold(
      () => {
        // Handle the case where there is no point
        console.log("No point available to show map modal.");
      },
      ({ maps, point }) => {
        // Call showMapModal only if there is a point
        showMapModal(maps, point, clientSpecificClass);
      }
    )
  );
}
